import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { CompanyProfile } from "@/admin/CompanyProfile";

export default function Policy() {
  const [policy, setPolicy] = useState(null);
  const [fx, setFx] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/policy").then((r) => setPolicy(r.data));
    api.get("/admin/fx").then((r) => setFx(r.data));
  }, []);

  const savePolicy = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/policy", {
        effective_from: policy.effective_from_input || null,
        note: policy.version_note || "",
        policy_pct: Number(policy.policy_pct),
        payout_day: Number(policy.payout_day || 10),
        default_credit_days: Number(policy.default_credit_days || 30),
        new_customer_bonus: Number(policy.new_customer_bonus),
        new_customer_threshold: Number(policy.new_customer_threshold),
        star_agent_bonus: Number(policy.star_agent_bonus),
        above_split: Number(policy.above_split),
        ownership_window_months: Number(policy.ownership_window_months),
        max_discount_level: Number(policy.max_discount_level),
        accelerator_tiers: policy.accelerator_tiers,
      });
      toast.success("Policy saved - product commission grids recalculated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveFx = async () => {
    try {
      await api.put("/admin/fx", { rates: fx.map((f) => ({ currency: f.currency, rate: Number(f.rate), symbol: f.symbol, effective_date: "2026-06-01" })) });
      toast.success("FX rates saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setTier = (i, key, v) => {
    const t = [...policy.accelerator_tiers];
    t[i] = { ...t[i], [key]: key === "boost" ? Number(v) : Number(v) };
    setPolicy({ ...policy, accelerator_tiers: t });
  };

  if (!policy) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div>
      <PageHeader title="Commission Policy & Settings" subtitle="Rates ship with company defaults — everything here is editable and applies live to both clients." />

      <CompanyProfile />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 lg:col-span-2 border-primary/30" data-testid="policy-pct-card">
          <h3 className="font-display font-semibold mb-1">Commission policy % of gross profit</h3>
          <p className="text-xs text-muted-foreground mb-3">Per-bag commission = policy % × (list price − cost) ÷ (1 + VAT). Changing this recalculates every product's 0–5% grid instantly; invoices already issued keep their frozen rates.</p>
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <F label="Policy % (0.24 = 24%)"><Input type="number" step="0.01" value={policy.policy_pct} onChange={(e) => setPolicy({ ...policy, policy_pct: e.target.value })} data-testid="policy-pct-input" /></F>
            <F label="Payout day (of next month)"><Input type="number" value={policy.payout_day ?? 10} onChange={(e) => setPolicy({ ...policy, payout_day: e.target.value })} data-testid="policy-payout-day-input" /></F>
            <F label="Default credit days (new customers)"><Input type="number" value={policy.default_credit_days ?? 30} onChange={(e) => setPolicy({ ...policy, default_credit_days: e.target.value })} data-testid="policy-credit-days-input" /></F>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Bonuses & rules</h3>
          <div className="grid grid-cols-2 gap-3">
            <F label="New-customer bonus (AED)"><Input type="number" value={policy.new_customer_bonus} onChange={(e) => setPolicy({ ...policy, new_customer_bonus: e.target.value })} data-testid="policy-new-customer-bonus" /></F>
            <F label="New-customer threshold (AED)"><Input type="number" value={policy.new_customer_threshold} onChange={(e) => setPolicy({ ...policy, new_customer_threshold: e.target.value })} /></F>
            <F label="Star-agent bonus (AED)"><Input type="number" value={policy.star_agent_bonus} onChange={(e) => setPolicy({ ...policy, star_agent_bonus: e.target.value })} /></F>
            <F label="Above-list split"><Input type="number" step="0.05" value={policy.above_split} onChange={(e) => setPolicy({ ...policy, above_split: e.target.value })} /></F>
            <F label="Ownership window (months)"><Input type="number" value={policy.ownership_window_months} onChange={(e) => setPolicy({ ...policy, ownership_window_months: e.target.value })} /></F>
            <F label="Max discount level (%)"><Input type="number" value={policy.max_discount_level} onChange={(e) => setPolicy({ ...policy, max_discount_level: e.target.value })} /></F>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Monthly accelerator tiers</h3>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground uppercase tracking-wider">
              <span>Min sales</span><span>Max sales</span><span>Boost</span>
            </div>
            {policy.accelerator_tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <Input type="number" value={t.min} className="h-9" onChange={(e) => setTier(i, "min", e.target.value)} />
                <Input type="number" value={t.max ?? ""} placeholder="∞" className="h-9" onChange={(e) => setTier(i, "max", e.target.value)} />
                <Input type="number" step="0.05" value={t.boost} className="h-9" onChange={(e) => setTier(i, "boost", e.target.value)} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-4 flex flex-wrap items-end justify-end gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Effective from (blank = today; future date schedules it)</Label><Input type="date" className="w-44" value={policy.effective_from_input || ""} onChange={(e) => setPolicy({ ...policy, effective_from_input: e.target.value })} data-testid="policy-effective-from-input" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Version note</Label><Input className="w-56" value={policy.version_note || ""} onChange={(e) => setPolicy({ ...policy, version_note: e.target.value })} data-testid="policy-version-note-input" /></div>
        <Button onClick={savePolicy} disabled={saving} data-testid="save-policy-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save policy"}
        </Button>
      </div>
      <PolicyVersions />

      <Card className="p-5 mt-6">
        <h3 className="font-display font-semibold mb-1">FX rates (1 AED → currency)</h3>
        <p className="text-xs text-muted-foreground mb-4">Agents are paid and shown figures in their own currency; analytics roll up to AED.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {fx.map((f, i) => (
            <div key={f.currency} className="space-y-1.5">
              <Label className="text-xs">{f.currency} ({f.symbol})</Label>
              <Input type="number" step="0.0001" value={f.rate} disabled={f.currency === "AED"} onChange={(e) => {
                const n = [...fx]; n[i] = { ...f, rate: e.target.value }; setFx(n);
              }} data-testid={`fx-${f.currency}`} />
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={saveFx} data-testid="save-fx-button">Save FX rates</Button>
        </div>
      </Card>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

function PolicyVersions() {
  const [vers, setVers] = useState(null);
  const load = () => api.get("/admin/policy/versions").then((r) => setVers(r.data));
  useEffect(() => { load(); }, []);
  const cancel = async (v) => {
    try { await api.delete(`/admin/policy/versions/${v.id}`); toast.success("Scheduled policy change cancelled"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!vers) return null;
  return (
    <Card className="p-5 mt-6" data-testid="policy-version-history">
      <h3 className="font-display font-semibold mb-1">Policy versions</h3>
      <p className="text-xs text-muted-foreground mb-3">Every saved change is kept with its effective date. Invoices reference the version in force on their date.</p>
      <div className="space-y-1.5 text-xs">
        {vers.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-2 border-b border-border/40 pb-1.5" data-testid={`policy-version-row-${v.id}`}>
            <span><Badge variant={v.status === "active" ? "default" : v.status === "scheduled" ? "secondary" : "outline"} className="mr-2 capitalize">{v.status}</Badge>
              from {v.effective_from}{v.effective_to ? ` to ${v.effective_to}` : ""} · policy {Math.round((v.snapshot?.policy_pct || 0) * 100)}% · NC bonus {v.snapshot?.new_customer_bonus} · star {v.snapshot?.star_agent_bonus}{v.note ? ` · ${v.note}` : ""} <span className="text-muted-foreground">({v.set_by})</span></span>
            {v.status === "scheduled" && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => cancel(v)} data-testid={`cancel-policy-version-${v.id}`}>Cancel</Button>}
          </div>
        ))}
      </div>
    </Card>
  );
}
